/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getSingleTemplate from 'api/template/getSingleTemplate'
import getTemplates, {
	GetTemplateParams,
	GetTemplateResponse,
} from 'api/template/getTemplates'
import { Template } from 'types'
import handleAxiosError from 'utils/handleAxiosError'

/**
 * Types for the slice of client in the store
 */
export interface TemplateState {
	records: Template[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
	singleTemplate: Template | null
	activeTemplates: Template[]
}

/**
 * Initial state of the slice client
 */
const initialState: TemplateState = {
	records: [],
	totalCount: 0,
	totalPages: 1,
	currentPage: 1,
	loading: false,
	singleTemplate: null,
	activeTemplates: [],
}

/**
 * Action for getting templates
 *
 * Tries to get templates from the API and returns the result in the payload
 *
 * @param {GetTemplateParams} options - Options for the API call
 * @returns {Promise<GetTemplateResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getTemplatesAction = createAsyncThunk<
	GetTemplateResponse,
	GetTemplateParams
>('template/getTemplatesAction', async (options, { rejectWithValue }) => {
	try {
		return await getTemplates(options)
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const getActiveTemplateAction = createAsyncThunk<
	GetTemplateResponse,
	void
>('templates/getActiveTemplateAction', async (_, { rejectWithValue }) => {
	try {
		const options = {
			page: 1,
			size: 250,
			status: 'APPROVED' as 'APPROVED',
			query: '',
		}
		return await getTemplates(options)
	} catch (error: any) {
		handleAxiosError(error)
		return rejectWithValue(error.message)
	}
})

export const getTemplateAction = createAsyncThunk<Template, string>(
	'template/getTemplateAction',
	async (id, { rejectWithValue }) => {
		try {
			return await getSingleTemplate(id)
		} catch (error: any) {
			return rejectWithValue(error.message)
		}
	}
)

export const templateSlice = createSlice({
	name: 'template',
	initialState,
	reducers: {
		clearTemplates: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getTemplatesAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getTemplatesAction.fulfilled, (state, action) => {
			state.loading = false
			const { response } = action.payload
			state.totalCount = response.totalCount
			state.records = response.records
			state.totalPages = response.totalPages
			state.currentPage = response.currentPage
		})
		builder.addCase(getTemplatesAction.rejected, state => {
			state.loading = false
		})
		builder.addCase(getTemplateAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getTemplateAction.fulfilled, (state, action) => {
			state.loading = false
			state.singleTemplate = action.payload
		})
		builder.addCase(getTemplateAction.rejected, state => {
			state.loading = false
		})
		/**
		 * Reducer for getting a list of active templates for forms
		 */
		builder.addCase(getActiveTemplateAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getActiveTemplateAction.fulfilled, (state, action) => {
			state.loading = false
			state.activeTemplates = action.payload.response.records
		})
		builder.addCase(getActiveTemplateAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { clearTemplates } = templateSlice.actions

export default templateSlice.reducer
